import { Box, Button } from '@mui/material';
import { Controller } from 'react-hook-form';
import { EditFnaStepComponent } from '../types';
import { FNADetails } from '../../../common/types/analyses';
import { JSX, useState } from 'react';
import { privateFulcrumApi } from '../../../common/lib/fulcrumApi';
import PreloadedClientList from '../../../clients/clientList/preloadedClientList';
import SelectClientsDialog from '../../../clients/selectClientsDialog';

interface RelatedParty {
  id: number;
  roleplayerID: number;
  partyRoleplayerID: number;
  relationshipID: number;
  relationshipName: string;
  relationshipNote: string;
  representativeRoleplayerID: number;
}

/**
 * Fetch related parties for roleplayer
 */
const getRelatedParties = (roleplayerId: number): Promise<RelatedParty[]> => {
  return privateFulcrumApi
    .get('/Partys', { params: { RoleplayerID: roleplayerId, IsPrimary: 1 } })
    .then(({ data }) => data);
};

/**
 * Step with FNA roleplayers (clients)
 */
const ClientsStep: EditFnaStepComponent = ({
  fna,
  control,
  setValue,
  commitFnaChanges,
}): JSX.Element => {
  const [selectDialogOpen, setSelectDialogOpen] = useState(false);

  const handleChangeRoleplayers = async (
    updatedRoleplayers: { roleplayerID: number }[],
  ): Promise<void> => {
    const fnaRoleplayer = updatedRoleplayers[0];
    if (fnaRoleplayer) {
      // if some roleplayers was selected, fetch related to the first roleplayer parties and set them to fna's parties field
      const relatedParties = await getRelatedParties(fnaRoleplayer.roleplayerID);
      setValue(
        'parties',
        relatedParties.map((item) => ({
          fnaid: fna.id,
          roleplayerID: item.roleplayerID,
          partyRoleplayerID: item.partyRoleplayerID,
          relationshipID: item.relationshipID,
          representativeRoleplayerID: item.representativeRoleplayerID,
        })) as FNADetails['parties'],
      );
    } else {
      // if no roleplayers then clear parties
      setValue('parties', []);
    }
    commitFnaChanges();
  };

  return (
    <Controller
      name="roleplayers"
      control={control}
      render={({ field: { onChange } }): JSX.Element => (
        <Box>
          <Box className="flex justify-end">
            <Button variant="outlined" onClick={(): void => setSelectDialogOpen(true)}>
              Choose client
            </Button>
          </Box>
          <PreloadedClientList
            enableQuickFilter={false}
            clients={fna.roleplayers}
            onClickDelete={(client): void => {
              // remove client from fna's roleplayers field
              const updatedRoleplayers = fna.roleplayers.filter(({ id }) => id !== client.id);
              onChange(updatedRoleplayers);
              handleChangeRoleplayers(updatedRoleplayers);
            }}
          />
          <SelectClientsDialog
            open={selectDialogOpen}
            onClose={(selectedIds?: number[]): void => {
              // if some client was selected, update fna's roleplayers field
              if (selectedIds) {
                const updatedRoleplayers = selectedIds.map((roleplayerID) => ({
                  roleplayerID,
                  fnaid: fna.id,
                }));
                onChange(updatedRoleplayers);
                handleChangeRoleplayers(updatedRoleplayers);
              }
              setSelectDialogOpen(false);
            }}
          />
        </Box>
      )}
    />
  );
};

export default ClientsStep;
